/* eslint-disable jsx-a11y/alt-text */
import * as React from "react"
import { Checkbox as CommonCheckbox, Flex, Text } from "@chakra-ui/react"
import { useRecoilValue } from "recoil"
import { UserAvatar } from "../../common/avatar"
import {
  brandingSelector
  // showInteractivityRequiredVisualSelector
} from "../../../recoil/atoms/room"
import { MessageInput } from "../message-input"
import { activeSpeakerSelector } from "../../../recoil/atoms/activeSpeaker"
import { accessList } from "./_user_modview"
import { SDKContext } from "../../../context/SDKContext"
import PrivateVideoConnection from "./private-video-connection"
import {
  AccessContainer,
  UserContainer,
  UserCollapseButton,
  CollapseButtonItems,
  ItemsContainer,
  ModeratorAvatar,
  UserName,
  ExpandedMenu,
  InputContainer
} from "./user-list.styled"
import { waitForElm } from "../../../util/waitForElm"
import { isPrivateRoomAtom } from "../../../recoil/atoms/privateRoom"
import { useTranslation } from "react-i18next"
import styled from "@emotion/styled"
import type { RoomBranding } from "../../../recoil/atoms/room/types"
import { useCurrentAvatarInfo } from "src/conditionals/Avatar"
import { currentChatGenerationAtom } from "src/recoil/atoms/chats"

interface StyledProps {
  roomStyles?: RoomBranding
}

const Checkbox = styled(CommonCheckbox)<StyledProps>`
  > span[data-checked] {
    background-color: ${({ roomStyles }) =>
      roomStyles?.servicePanelElementBorderOffset || "#fff"};
    border-color: ${({ roomStyles }) =>
      roomStyles?.servicePanelElementBorder ?? "gray"};
    color: ${({ roomStyles }) =>
      roomStyles?.servicePanelElementBorder ?? "gray"};

    /*  &:hover {
      background-color: none;
    } */
  }

  > span[data-checked]:hover {
    background-color: transparent;
    border-color: transparent;
  }

  svg {
    font-size: 0.5rem;
  }
`

interface UserPanelProps {
  userId: string | number
  isLast: boolean
  expandedStyles?: React.CSSProperties
  expandedMenu: boolean
  userName: string
  handRaisedDisplay: React.ReactNode
  micDisplay: React.ReactNode
  camDisplay: React.ReactNode
  accessList?: accessList[]

  color: string
  avatar: string
  isAudience: boolean
  isModerator: boolean
}

const _UserPanel: React.FC<UserPanelProps> = ({
  isLast,
  userId,
  expandedStyles,
  expandedMenu,
  userName,
  handRaisedDisplay,
  micDisplay,
  camDisplay,
  accessList,
  color,
  avatar,
  isAudience,
  isModerator
}) => {
  const { t } = useTranslation()
  const [positionModal, setPositionModal] = React.useState({ x: null, y: null })
  const [isEnabled, setEnabled] = React.useState(false)
  const roomStyles = useRecoilValue(brandingSelector)
  const activeSpeaker = useRecoilValue(activeSpeakerSelector)
  const meetingClient = React.useContext(SDKContext)
  const isPrivateMeeting = useRecoilValue(isPrivateRoomAtom)
  const avatarConfig = useCurrentAvatarInfo()
  // const showInteractivityRequiredVisual = useRecoilValue(
  //   showInteractivityRequiredVisualSelector
  // )

  const backgroundPanel = React.useMemo(() => {
    return roomStyles?.servicePanelElementBackground ?? "white"
  }, [roomStyles?.servicePanelElementBackground])

  const handleAssignPosition = React.useCallback(event => {
    setPositionModal({ x: `${1.5}px`, y: event.clientY + 160 })
  }, [])

  const border = React.useMemo<string>(() => {
    if (userId === activeSpeaker.uid) {
      return `2px solid ${roomStyles.highlighterColor ?? "#000000"}`
    }

    if (userId !== activeSpeaker.uid) {
      return roomStyles?.servicePanelElementBorder
        ? `2px solid ${roomStyles?.servicePanelElementBorder}`
        : "2px solid #E2E8F0"
    }
  }, [
    activeSpeaker.uid,

    roomStyles?.highlighterColor,
    roomStyles?.servicePanelElementBorder,
    userId
  ])

  const generation = useRecoilValue(currentChatGenerationAtom)

  const handleSendPrivateMessage = React.useCallback(
    (message: string, parentChatId: string, userId: any) => {
      if (!message.length) return
      meetingClient?.sendPublicChat(
        message,
        parentChatId,
        userId,
        null,
        avatarConfig?.id.toString(),
        generation
      )
    },
    [meetingClient]
  )

  const toggleAccordion = async () => {
    if (expandedMenu) {
      if (!isEnabled) {
        setEnabled(true)
      }

      const element = (await waitForElm(
        `expanding-user-${userId}`
      )) as HTMLDivElement

      if (
        element.style.maxHeight !== "0px" &&
        element.style.maxHeight !== undefined &&
        element.style.maxHeight !== ""
      ) {
        element.style.display = "none"
        element.style.maxHeight = "0px"
      } else {
        element.style.display = "block"
        element.style.maxHeight = element.scrollHeight + "px"
      }
    }
  }

  return (
    <UserContainer
      backgroundPanel={backgroundPanel}
      border={border}
      isLast={isLast}
      key={userId}
      expandedMenu={expandedMenu}
    >
      <UserCollapseButton onClick={toggleAccordion} expandedMenu={expandedMenu}>
        <CollapseButtonItems>
          <ItemsContainer>
            {avatar !== "" &&
            avatar !== undefined &&
            avatar !== null &&
            avatar.startsWith("http") ? (
              <ModeratorAvatar data={avatar} width="32" height="32" />
            ) : (
              <UserAvatar
                name={userName}
                size="sm"
                src={avatar}
                colorDisk={color}
                avatar={avatar}
                avatarStyle={{ width: "2rem", height: "2rem" }}
              />
            )}

            <UserName roomStyles={roomStyles}>{userName}</UserName>
          </ItemsContainer>
          <ItemsContainer style={{ alignItems: "baseline" }}>
            {handRaisedDisplay}
            {micDisplay}
            {camDisplay}
          </ItemsContainer>
        </CollapseButtonItems>
      </UserCollapseButton>
      {expandedMenu && isEnabled && (
        <ExpandedMenu id={`expanding-user-${userId}`}>
          <AccessContainer>
            {accessList.map((access, index) => (
              <div key={index} id="test">
                <Flex alignItems="center">
                  <Checkbox
                    roomStyles={roomStyles}
                    isChecked={access.hasPermission}
                    onChange={() => access.togglePermission()}
                    value={access.value}
                    alignItems="center"
                    justifyContent="center"
                    border="2px solid"
                    fontSize="0.5rem"
                    borderColor={
                      roomStyles?.servicePanelElementBorder ?? "gray"
                    }
                    color={roomStyles?.servicePanelElementBorder ?? "gray"}
                    /* borderRadius={props.ratiobutton === "true" ? "50%" : "0.125rem"} */
                    w={4}
                    bg={roomStyles?.servicePanelElementBorderOffset}
                    h={4}
                    // isDisabled={Boolean(showInteractivityRequiredVisual)}
                    _hover={{}}
                  />
                  <Text
                    fontSize={"sm"}
                    marginLeft="0.5rem"
                    color={
                      roomStyles?.servicePanelElementPrimaryText &&
                      roomStyles.servicePanelElementPrimaryText
                    }
                  >
                    {access.name}
                  </Text>
                </Flex>
              </div>
            ))}
          </AccessContainer>
          <InputContainer>
            <MessageInput
              positionModal={positionModal}
              callbackEmoji={handleAssignPosition}
              inputId={`expanding-input-user-${userId}`}
              parentMessageId=""
              userId={userId}
              sendMessage={handleSendPrivateMessage}
              noAttachment
              placeholder={t("Send a private message...")}
              inputLeftElementStyle={{
                paddingRight: "7px",
                top: "inherit",
                color: "gray.500"
              }}
              inputRightElementStyle={{
                top: "inherit",
                paddingTop: "0.2rem",
                alignItems: "center"
              }}
              stylesEmodjiContainer={{ width: "280px" }}
            />
          </InputContainer>
          {!isPrivateMeeting && (
            <PrivateVideoConnection
              expandId={`expanding-user-${userId}`}
              userId={userId}
            />
          )}
        </ExpandedMenu>
      )}
    </UserContainer>
  )
}

export const UserPanel = React.memo(_UserPanel)
